import React from "react";
import axios from "axios";
import NotificationManager from "../utils/notificationManager";
import { mergeCachedResource, NameSyncTracker } from "../utils/nameSyncState";

const CACHE_TTL_MS = 60_000*5; // 1 minute in milliseconds

const resourceRef = {
  outlets: "Outlet",
  inlets: "Inlet",
  devices: "Device",
  groups: "Group",
  enterprises: "Enterprise",
  events: "Event",
  eventLogs: "Event Log",
  actions: "Action",
  roles: "Role",
  activity: "Activity Log",
};

function fetchWrapper(WrappedComponent) {
  return class extends React.Component {
    constructor(props) {
      super(props);
      this.cancelTokens = {};
    }
    nameSyncTracker = new NameSyncTracker();

    updateCachedResourceItem = (resource, id, patch, idField = "id") => {
      if (resource !== "devices" && resource !== "outlets") {
        this.setState((state) => ({ [resource]: (state[resource] || []).map((item) =>
          item[idField] == id ? { ...item, ...patch } : item) }));
        return;
      }
      this.nameSyncTracker.remember(resource, id, patch);
      this.setState((state) => mergeCachedResource(state, resource, id, patch, idField));
    };

    state = {
      outlets: [],
      outletsPages: [],
      outletsMeta: {},
      outletsRequestsInFlight: 0,
      outletsPageLoading: {},
      outletsPaginationResetKey: 0,
      outletsLoading: false,
      outletsLoaded: false,
      // outletsLastLoaded: null,

      inlets: [],
      inletsPages: [],
      inletsMeta: {},
      inletsRequestsInFlight: 0,
      inletsPageLoading: {},
      inletsPaginationResetKey: 0,
      inletsLoading: false,
      inletsLoaded: false,
      inletsLastLoaded: null,

      devices: [],
      devicesLoading: false,
      devicesLoaded: false,
      devicesPages: [
        /**
         * {
         *  page: number,
         *  devices: []
         * }
         */
      ],
      devicesMeta: {},
      devicesPageLoading: {},
      devicesPaginationResetKey: 0,
      // devicesLastLoaded: null,
      resourcePageCache: {
      },

      events: [],
      eventsLoading: false,
      eventsLoaded: false,

      eventLogs: [],
      eventLogsPages: [],
      eventLogsMeta: {},
      eventLogsRequestsInFlight: 0,
      eventLogsPageLoading: {},
      eventLogsPaginationResetKey: 0,
      eventLogsLoading: false,
      eventLogsLoaded: false,

      eventItemLoading: false,

      actions: [],
      actionsLoading: false,
      actionsLoaded: false,

      sensors: [],
      sensorsLoading: false,
      sensorsLoaded: false,

      banksLoading: false,
      banksLoaded: false,
      banks: [],

      groups: [],
      groupsLoading: false,
      groupsLoaded: false,

      activity: [],
      activityPages: [],
      activityMeta: {},
      activityRequestsInFlight: 0,
      activityPageLoading: {},
      activityPaginationResetKey: 0,
      activityLoading: false,
      activityLoaded: false,

      enterprises: [],
      enterprisesLoading: false,
      enterprisesLoaded: false,

      userEnterprise: {},
      userEnterpriseLoading: false,
      userEnterpriseLoaded: false,
      userEnterpriseUsers: [],

      roles: [],
      rolesLoading: false,
      rolesLoaded: false,

      conf: [],
      confLoading: false,
      confLoaded: false,

      fwStatus: [
        // {
        //  enclosureSerialNumber: "",
        //  fwStatus: null,
        //  loading: false,
        //  loaded: false,
        //  lastUpdated: null
        // }
      ],
      // fwStatusLoading: false,
      // fwStatusLoaded: false,
    };
    // Helper to build a key from queryParams
    buildCacheKey = (queryParams = {}) => {
      return Object.keys(queryParams)
        .sort() // ensure consistent ordering
        .map((k) => `${k}=${queryParams[k]}`)
        .join("&");
    };

    fetchResource = (
      resource,
      cb = () => {},
      queryParam = "",
      version = "v1"
    ) => {
      this.setState({ [`${resource}Loading`]: true });
      axios
        .get(`/${version}/${resource}${queryParam}`, {
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
        })
        .then((res) => {
          this.setState({
            [`${resource}Loading`]: false,
            [`${resource}Loaded`]: true,
            // [`${resource}LastLoaded`]: new Date(),
          });
          cb(null, res);
          if (resource === "devices" || resource === "events") {
            this.setState({
              [resource]: res.data.data.map((d) => ({
                ...d,
                lastUpdated: new Date(),
              })),
            });
          } else {
            this.setState({ [resource]: res.data.data });
          }
        })
        .catch((error) => {
          this.setState({
            [`${resource}Loading`]: false,
            [`${resource}Loaded`]: true,
          });
          cb(error, null);
        });
    };

    cancelPaginatedRequestsForResource = (resource) => {
      this.setState({
        [`${resource}PaginationResetKey`]:
          this.state[`${resource}PaginationResetKey`] + 1,
      });
      if (this.cancelTokens[resource]) {
        Object.values(this.cancelTokens[resource]).forEach((cancelFunc) => {
          if (typeof cancelFunc.cancel === "function") {
            cancelFunc.cancel(`Canceling in-flight requests for ${resource}`);
          } else if (typeof cancelFunc === "function") {
            cancelFunc("Canceling in-flight requests for ${resource}");
          }
        });
        this.cancelTokens[resource] = {};
      }
    };
    // Modified fetchResourcePaginate to include caching logic
    fetchResourcePaginate = (
      resource,
      cb = () => {},
      queryParams = {},
      resetPages = false,
      ignoreCache = false,
    ) => {
      if (!queryParams.page) {
        console.log("Missing 'page' in queryParams");
        return;
      }
      if (!queryParams.limit) {
        queryParams.limit = 10;
      }
    
      // Create / store a new cancel token per resource + page
      if (!this.cancelTokens[resource]) {
        this.cancelTokens[resource] = {};
      }
    
      // If resetPages=true, cancel existing calls & reset pages
      if (resetPages) {
        this.cancelPaginatedRequestsForResource(resource);
        this.setState({ [`${resource}Pages`]: [] });
      }
    
      const nameReadRevision = this.nameSyncTracker.revision;
      const source = axios.CancelToken.source();
      const cacheKey = this.buildCacheKey(queryParams); // e.g. 'page=2&limit=10&filter=x'
      this.cancelTokens[resource][cacheKey] = source;
    
      // Check the cache
      const resourceCache = this.state.resourcePageCache[resource] || {};
      const cachedEntry = resourceCache[cacheKey];
      const now = Date.now();
    
      // If we have a valid cached entry, respond with it immediately
      if (cachedEntry && now - cachedEntry.lastFetched < CACHE_TTL_MS && !ignoreCache // If ignoreCache is false, use the cache
      ) {
        // Use cached data

    
        // Immediately call the callback with the cached data
        cb(null, {
          data: {
            data: cachedEntry.data,
            meta: cachedEntry.meta,
          },
        });
    
        // Update the resourcePages from cache
        this.setState((prevState) => {
          // Remove any existing entry for this page:
          const filteredPages = (prevState[`${resource}Pages`] || []).filter(
            (p) => p.page !== cachedEntry.page
          );
    
          // Insert the cached page
          const updatedPages = [
            ...filteredPages,
            {
              page: cachedEntry.page,
              // For example, 'devices' if resource === "devices"
              // or 'events' if resource === "events", etc.
              [resource]: cachedEntry.data,
            },
          ];

          return {
            [`${resource}Pages`]: updatedPages,
            [`${resource}Meta`]: cachedEntry.meta,
          };
        });

        // Stop here if you do NOT want to do a fresh fetch
        return;
      }
    
      // Otherwise, do a network fetch
      this.setState((prevState) => {
        const currentInFlight = prevState[`${resource}RequestsInFlight`] || 0;
        return {
          [`${resource}RequestsInFlight`]: currentInFlight + 1,
          [`${resource}PageLoading`]: {
            ...prevState[`${resource}PageLoading`],
            [queryParams.page]: true,
          },
        };
      });
    
      const stringifiedQueryParams = Object.keys(queryParams)
        .map((key) => `${key}=${queryParams[key]}`)
        .join("&");
    
      axios
        .get(
          `/v2/${resource}${
            stringifiedQueryParams ? `?${stringifiedQueryParams}` : ""
          }`,
          {
            headers: {
              Authorization: "Bearer " + localStorage.getItem("token"),
            },
            cancelToken: source.token,
          }
        )
        .then((res) => {
          const newData = res.data.data.map((d) => ({
            ...this.nameSyncTracker.apply(resource, d, nameReadRevision),
            lastUpdated: new Date(),
          }));
    
          this.setState((prevState) => {
            const existingData = [...(prevState[resource] || [])];
    
            // Merge or append any items if needed. (Optional merging)
            newData.forEach((dataItem) => {
              const index = existingData.findIndex(
                (ed) => ed.enclosureSerialNumber === dataItem.enclosureSerialNumber
              );
              if (index > -1) {
                existingData[index] = {
                  ...existingData[index],
                  ...dataItem,
                };
              } else {
                existingData.push(dataItem);
              }
            });
    
            // Update resourcePages to hold this new page
            const oldPages = prevState[`${resource}Pages`] || [];
            const filteredPages = oldPages.filter(
              (p) => p.page !== queryParams.page
            );
            const nextPages = [
              ...filteredPages,
              {
                page: Number(queryParams.page),
                [resource]: newData,
              },
            ];
    
            // Decrement requestsInFlight
            const nextInFlight =
              (prevState[`${resource}RequestsInFlight`] || 0) - 1;
    
            // Cache the result
            return {
              [resource]: existingData,
              [`${resource}Pages`]: nextPages,
              [`${resource}Meta`]: res.data.meta,
              [`${resource}PageLoading`]: {
                ...prevState[`${resource}PageLoading`],
                [queryParams.page]: false,
              },
              [`${resource}RequestsInFlight`]: nextInFlight,
              [`${resource}Loaded`]: true,
              resourcePageCache: {
                ...prevState.resourcePageCache,
                [resource]: {
                  ...prevState.resourcePageCache[resource],
                  [cacheKey]: {
                    lastFetched: now,
                    page: Number(queryParams.page),
                    data: newData,
                    meta: res.data.meta,
                  },
                },
              },
            };
          });
    
          cb(null, res);
        })
        .catch((error) => {
          console.log(error);
          if (axios.isCancel(error)) {
            console.log(
              `Request canceled for ${resource} queryKey = ${cacheKey}:`,
              error.message
            );
            this.setState((prevState) => {
              const nextInFlight =
                (prevState[`${resource}RequestsInFlight`] || 0) - 1;
              return {
                [`${resource}RequestsInFlight`]: Math.max(nextInFlight, 0),
                [`${resource}PageLoading`]: {
                  ...prevState[`${resource}PageLoading`],
                  [queryParams.page]: false,
                },
                [`${resource}Loading`]: nextInFlight > 0,
              };
            });
          } else {
            this.setState((prevState) => {
              const nextInFlight =
                (prevState[`${resource}RequestsInFlight`] || 0) - 1;
              return {
                [`${resource}Loading`]: nextInFlight > 0,
                [`${resource}Loaded`]: true,
                [`${resource}RequestsInFlight`]: nextInFlight,
                [`${resource}PageLoading`]: {
                  ...prevState[`${resource}PageLoading`],
                  [queryParams.page]: false,
                },
              };
            });
            cb(error, null);
          }
        });
    };
    // Modified resetResourcePages to cancel any in-flight fetchResourcePaginate
    resetResourcePages = (resource) => {
      // console.log("Reseting resource pages")
      this.cancelPaginatedRequestsForResource(resource);
      // Clear out any existing pages in state
      this.setState({
        [`${resource}Pages`]: [],
        [`${resource}Loading`]: false, // Reset resource loading to false
        [`${resource}PageLoading`]: {}, // Clear any page-based loading states
        [`${resource}RequestsInFlight`]: 0, // No requests are now in flight
        // Optionally reset `${resource}Loaded` if you want to consider them not loaded
        // [`${resource}Loaded`]: false,
      });
    };

    fetchResourceItem = (resource, id, cb = () => {}, replacementUrl) => {
      this.setState({
        [`${resource}ItemLoading`]: true,
      });
      axios
        .get(replacementUrl || `/v1/${resource}/${id}`, {
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
        })
        .then((res) => {
          this.setState({
            [`${resource}ItemLoading`]: false,
          });
          cb(null, res);
          this.setState({
            [resource]: this.state[resource].map((item) =>
              item.id == id
                ? {
                    ...res.data.data,
                    lastUpdated: new Date(),
                  }
                : item
            ),
          });
        })
        .catch((err) => {
          this.setState({
            [`${resource}ItemLoading`]: false,
          });
          cb(err, null);
          console.log(err);
        });
    };
    fetchFwStatus = async (enclosureSerialNumber) => {
      console.log("fetching fw status for", enclosureSerialNumber);
      let currentFwStatuses = this.state.fwStatus;
      const foundFwStatus = currentFwStatuses.find(
        (f) => f.enclosureSerialNumber == enclosureSerialNumber
      );
      if (foundFwStatus) {
        console.log("found fw status for", enclosureSerialNumber);
        this.setState({
          // update specific device with loading
          fwStatus: currentFwStatuses.map((f) => {
            if (f.enclosureSerialNumber == enclosureSerialNumber) {
              console.log("updating fw status for", enclosureSerialNumber);
              return {
                ...f,
                loading: true,
              };
            } else {
              return f;
            }
          }),
        });
      } else {
        currentFwStatuses.push({
          enclosureSerialNumber: enclosureSerialNumber,
          loading: true,
        });
        this.setState({
          fwStatus: currentFwStatuses,
        });
      }
      try {
        const fwStatus = await axios.get(
          `/v1/devices/${enclosureSerialNumber}/fwUpdateStatus`,
          {
            headers: {
              Authorization: "Bearer " + localStorage.getItem("token"),
            },
          }
        );
        this.setState({
          fwStatus: this.state.fwStatus.map((f) => {
            if (f.enclosureSerialNumber == enclosureSerialNumber) {
              return {
                ...f,
                loading: false,
                loaded: true,
                fwStatus: fwStatus.data,
                lastUpdated: new Date(),
              };
            } else {
              return f;
            }
          }),
        });
      } catch (err) {
        console.log(err);
        this.setState({
          fwStatus: this.state.fwStatus.map((f) => {
            if (f.enclosureSerialNumber == enclosureSerialNumber) {
              return {
                ...f,
                loading: false,
                loaded: true,
                fwStatus: null,
              };
            } else {
              return f;
            }
          }),
        });
      }
    };
    createResource = (resource, body, cb = () => {}, replacementUrl) => {
      axios
        .post(replacementUrl || `/v1/${resource}`, body, {
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
        })
        .then((res) => {
          cb(null, res.data.data);
          this.setState({
            [resource]: [...this.state[resource], res.data.data],
          });
          NotificationManager.success(
            `${resourceRef[resource]} created successfully`
          );
        })
        .catch((err) => {
          console.log(err);
          NotificationManager.alert(
            `Error occurred creating ${resourceRef[resource]}`
          );

          cb(err, null);
        });
    };
    removeResource = (
      resource,
      resourceId,
      cb = () => {},
      idField = "id",
      replacementUrl = null
    ) => {
      axios
        .delete(replacementUrl || `/v1/${resource}/${resourceId}`, {
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
        })
        .then((res) => {
          cb(null, res.data.data);
          this.setState({
            [resource]: this.state[resource].filter(
              (f) => f[idField] != resourceId
            ),
          });
          NotificationManager.success(
            `${resourceRef[resource]} deleted successfully`
          );
        })
        .catch((err) => {
          console.log(err);
          cb(err, null);
          NotificationManager.alert(
            `Error occurred deleting ${resourceRef[resource]}`
          );
        });
    };
    // fetchAndUpdateResourceItem = (resource, id, cb = ()=>{}) => {
    //   axios.get(`/v1/${resource}/${id}`)
    //     .then(res => {
    //       cb(null, res);
    //       this.setState({
    //         [resource]: this.state[resource.map()]
    //       })
    //     }).catch(err => {
    //       cb(err, null);
    //       console.log(err);
    //     })
    // }

  updateOutletItem = (outletId, body, cb = () => {}) => {
    axios
      .put(`/v1/outlets/${outletId}`, body, {
        headers: { Authorization: "Bearer " + localStorage.getItem("token") },
      })
      .then(res => {
        const incoming = {
          ...res.data.data,          // what the backend returned
          lastUpdated: new Date(),   // stamp it
          loading: false,
        };
        this.updateCachedResourceItem("outlets", outletId, incoming, "outlet_id");
        cb(null, res.data.data);
        NotificationManager.success(res.data.message || (incoming.nameSyncStatus === "pending"
          ? "Outlet name saved. Pending sync."
          : "Outlet updated successfully"));
      })
      .catch(err => {
        const data = err.response?.data?.data;
        if (data?.nameSyncStatus) this.updateCachedResourceItem("outlets", outletId, data, "outlet_id");
        cb(err, null);
        console.error(err);
        NotificationManager.alert("Error occurred updating Outlet");
      });
  };

  getOutletItemAndData = (id, cb = () => {}, ignoreLoading) => {
    const nameReadRevision = this.nameSyncTracker.revision;
    axios.get(`/v1/outlets/${id}`, {
      headers: {
        Authorization: "Bearer " + localStorage.getItem("token"),
      },
    })
      .then(res => {
        res.data.data = this.nameSyncTracker.apply("outlets", res.data.data, nameReadRevision);
        cb(null, res);
        const newOutlet = {
          ...res.data.data,
          lastUpdated: new Date(),
          loading: false,
        };
        this.setState(prev => {
          const replace = o =>
            o.outlet_id === newOutlet.outlet_id ? {...o, ...newOutlet} : o;

          const updatedPages = (prev.outletsPages || []).map(p => ({
            ...p,
            outlets: (p.outlets || []).map(replace),
          }));

          const updatedResourceCache = (() => {
            const rc = prev.resourcePageCache.outlets || {};
            const out = {};
            for (const k in rc) {
              const entry = rc[k];
              out[k] = {
                ...entry,
                data: entry.data.map(replace),
              };
            }
            return { ...prev.resourcePageCache, outlets: out };
          })();

          return {
            outlets: prev.outlets.map(replace),
            outletsPages: updatedPages,
            resourcePageCache: updatedResourceCache,
          };
        });
      }
      )
      .catch(err => {
        cb(err, null);
        console.log(err);
      });
  }

updateDeviceItem = (enclosureSerialNumber, body, cb = () => {}) => {
  axios
    .put(`/v1/devices/${enclosureSerialNumber}`, body, {
      headers: { Authorization: "Bearer " + localStorage.getItem("token") },
    })
    .then(res => {
      const incoming = {
        ...res.data.data,          // what the backend returned
        lastUpdated: new Date(),   // stamp it
        loading: false,
      };

      this.updateCachedResourceItem("devices", enclosureSerialNumber, incoming, "enclosureSerialNumber");

      cb(null, res.data.data);
      NotificationManager.success(
        res?.data?.message || "Device updated successfully"
      );
    })
    .catch(err => {
      const data = err.response?.data?.data;
      if (data?.nameSyncStatus) this.updateCachedResourceItem("devices", enclosureSerialNumber, data, "enclosureSerialNumber");
      cb(err, null);
      console.error(err);
      NotificationManager.alert("Error occurred updating Device");
    });
};



    getDeviceItemAndData = (id, cb = () => {}, ignoreLoading) => {
      const nameReadRevision = this.nameSyncTracker.revision;
      const foundDevice = this.state.devices.find(
        (d) => d.enclosureSerialNumber === id
      );
      if (foundDevice) {
        this.setState({
          devices: this.state.devices.map((d) =>
            d.enclosureSerialNumber === id
              ? { ...d, loading: ignoreLoading ? false : true }
              : d
          ),
        });
      }

      const token = localStorage.getItem("token");
      const encodedId = encodeURIComponent(id);

      const processDevice = (deviceData, { offline = false, message = null } = {}) => {
        deviceData = this.nameSyncTracker.apply("devices", deviceData, nameReadRevision);
        const responsePayload = { data: { data: deviceData || null } };
        if (offline) {
          responsePayload.offline = true;
          if (message) {
            responsePayload.message = message;
          }
        }

        if (!deviceData) {
          cb(null, responsePayload);
          return;
        }

        const normalizedDevice = {
          ...deviceData,
          // outlets: Array.isArray(deviceData.outlets) ? deviceData.outlets : [],
          // inlets: Array.isArray(deviceData.inlets) ? deviceData.inlets : [],
          // banks: Array.isArray(deviceData.banks) ? deviceData.banks : [],
          // sensors: Array.isArray(deviceData.sensors) ? deviceData.sensors : [],
          // events: Array.isArray(deviceData.events) ? deviceData.events : [],
          lastUpdated: new Date(),
          loading: false,
        };

        this.setState((prev) => {
          const replace = (device) =>
            device.enclosureSerialNumber === normalizedDevice.enclosureSerialNumber
              ? { ...device, ...normalizedDevice }
              : device;

          const updatedPages = (prev.devicesPages || []).map((page) => ({
            ...page,
            devices: (page.devices || []).map(replace),
          }));

          const updatedResourceCache = (() => {
            const rc = prev.resourcePageCache.devices || {};
            const out = {};
            for (const key in rc) {
              const entry = rc[key];
              out[key] = {
                ...entry,
                data: entry.data.map(replace),
              };
            }
            return { ...prev.resourcePageCache, devices: out };
          })();

          return {
            devices: prev.devices.map(replace),
            devicesPages: updatedPages,
            resourcePageCache: updatedResourceCache,
          };
        });

        cb(null, { ...responsePayload, data: { data: normalizedDevice } });
      };

      axios
        .get(`/v2/devices/${encodedId}/live`, {
          headers: {
            Authorization: "Bearer " + token,
          },
        })
        .then((res) => {
          processDevice(res?.data?.data || null);
        })
        .catch((err) => {
          const status = err?.response?.status;
          const code = err?.response?.data?.code;
          if (status === 503 && code === "DEVICE_OFFLINE") {
            processDevice(err.response?.data?.data || null, {
              offline: true,
              message: err.response?.data?.message,
            });
            return;
          }

          cb(err, null);
          console.log(err);
        });
    };

    modifyResource = (
      resource,
      id,
      body,
      cb = () => {},
      itemIdfield = "id",
      noNotification,
      replacementUrl
    ) => {
      axios
        .put(replacementUrl || `/v1/${resource}/${id}`, body, {
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
        })
        .then((res) => {
          this.updateCachedResourceItem(resource, id, res.data.data, itemIdfield);
          cb(null, res.data);
          if (!noNotification) {
            NotificationManager.success(
              res?.data?.message ||
                `${resourceRef[resource] || resource} updated successfully`
            );
          }
        })
        .catch((err) => {
          const data = err.response?.data?.data;
          if (data?.nameSyncStatus) this.updateCachedResourceItem(resource, id, data, itemIdfield);
          cb(err, null);
          console.log(err);
          if (!noNotification) {
            NotificationManager.alert(
              `Error occurred updating ${resourceRef[resource]}`
            );
          }
        });
    };

    updateFetchWrapperState = (newState) => {
      this.setState(newState);
    };

    componentDidMount() {
      // this.fetchResourcePaginate("devices", () => {
      //   this.fetchResourcePaginate("devices", undefined, {page: 2, limit: 10 })
      //   this.fetchResourcePaginate("devices", undefined, {page: 3, limit: 10 })
      // }, {page: 1, limit: 10 })
      // check if user is enterprise admin or has_admin_privileges to fetch enterprise  by user.enterprise_Id
      if (
        this.props.user.enterprise_Id &&
        (this.props.user.is_enterprise_admin ||
          this.props.user.has_admin_privileges)
      ) {
        this.setState({ userEnterpriseLoading: true });
        axios
          .get("/v1/enterprises/" + this.props.user.enterprise_Id, {
            headers: {
              Authorization: "Bearer " + localStorage.getItem("token"),
            },
          })
          .then((res) => {
            this.setState({
              userEnterprise: res.data.data,
              userEnterpriseLoading: false,
              userEnterpriseLoaded: true,
            });
          })
          .catch((err) => {
            console.log(err);
            this.setState({
              userEnterpriseLoading: false,
              userEnterpriseLoaded: true,
            });
          });
      }
    }

    render() {
      return (
        <WrappedComponent
          resources={this.state}
          fetchResource={this.fetchResource}
          resetResourcePages={this.resetResourcePages}
          fetchResourcePaginate={this.fetchResourcePaginate}
          fetchResourceItem={this.fetchResourceItem}
          modifyResource={this.modifyResource}
          updateCachedResourceItem={this.updateCachedResourceItem}
          removeResource={this.removeResource}
          createResource={this.createResource}
          getDeviceItemAndData={this.getDeviceItemAndData}
          updateFetchWrapperState={this.updateFetchWrapperState}
          fetchFwStatus={this.fetchFwStatus}
          updateDeviceItem={this.updateDeviceItem}
          updateOutletItem={this.updateOutletItem}
          getOutletItemAndData={this.getOutletItemAndData}
          {...this.props}
        />
      );
    }
  };
}

export default fetchWrapper;
